import {
  CookiePreferences,
  readCookiePreferences,
} from './cookiePreferences';

const AUTOMATIC_TRACKING_ROUTES = new Set(['/', '/contact', '/privacy-policy']);
const CONVERSION_TRACKING_ROUTE = '/subscription-confirmed';
const EVENT_ONLY_GOOGLE_TRACKING_ROUTES = new Set([
  CONVERSION_TRACKING_ROUTE,
  '/business-setup',
]);
const PRODUCTION_HOSTS = new Set(['lovibook.com', 'www.lovibook.com']);
const GOOGLE_ADS_ID = 'AW-18438481741';

type TrackingWindow = Window & {
  clarity?: (...args: unknown[]) => void;
  dataLayer?: unknown[][];
  fbq?: (...args: unknown[]) => void;
  gtag?: (...args: unknown[]) => void;
  ttq?: {
    page?: () => void;
    grantConsent?: () => void;
    revokeConsent?: () => void;
  };
};

let lastGoogleTrackedPath = '';
let lastMarketingTrackedPath = '';
let googleAdsInitialized = false;
let googleTrackingReady = false;
let marketingTrackingReady = false;
let trackingGeneration = 0;
let trackerLoadStarted = false;
const pendingMarketingEvents: Array<{
  event: () => void | Promise<void>;
  resolve: () => void;
}> = [];
const pendingGoogleEvents: Array<{
  event: () => void | Promise<void>;
  resolve: () => void;
}> = [];
const scriptLoads = new Map<string, Promise<void>>();

function trackingWindow(): TrackingWindow {
  return window as TrackingWindow;
}

export function isSafeMarketingRoute(pathname: string): boolean {
  return AUTOMATIC_TRACKING_ROUTES.has(pathname);
}

export function isThirdPartyTrackingRoute(pathname: string): boolean {
  return isSafeMarketingRoute(pathname) || pathname === CONVERSION_TRACKING_ROUTE;
}

export function isThirdPartyTrackingLoaded(): boolean {
  const currentWindow = trackingWindow();
  return trackerLoadStarted
    || Boolean(currentWindow.fbq || currentWindow.ttq || currentWindow.clarity || currentWindow.gtag);
}

export function runMarketingTrackingEvent(event: () => void | Promise<void>): Promise<void> {
  const preferences = readCookiePreferences();
  if (!PRODUCTION_HOSTS.has(window.location.hostname)
      || (!isSafeMarketingRoute(window.location.pathname)
        && window.location.pathname !== CONVERSION_TRACKING_ROUTE)
      || !preferences.marketing) {
    return Promise.resolve();
  }
  if (marketingTrackingReady) {
    return Promise.resolve(event());
  }
  const pendingEvent = new Promise<void>((resolve) => {
    pendingMarketingEvents.push({ event, resolve });
  });
  void enableMarketingTracking(window.location.pathname, trackingGeneration).catch(() => {
    pendingMarketingEvents.splice(0).forEach(({ resolve }) => resolve());
  });
  return pendingEvent;
}

export function runGoogleConsentModeEvent(event: () => void | Promise<void>): Promise<void> {
  if (!PRODUCTION_HOSTS.has(window.location.hostname)
      || !EVENT_ONLY_GOOGLE_TRACKING_ROUTES.has(window.location.pathname)) {
    return Promise.resolve();
  }
  if (googleTrackingReady) {
    return Promise.resolve(event());
  }
  const pendingEvent = new Promise<void>((resolve) => {
    pendingGoogleEvents.push({ event, resolve });
  });
  void enableGoogleConsentMode(window.location.pathname, trackingGeneration).catch(() => {
    pendingGoogleEvents.splice(0).forEach(({ resolve }) => resolve());
  });
  return pendingEvent;
}

function loadScript(id: string, src: string): Promise<void> {
  const pendingLoad = scriptLoads.get(id);
  if (pendingLoad) {
    return pendingLoad;
  }
  const existingScript = document.getElementById(id) as HTMLScriptElement | null;
  if (existingScript) {
    return Promise.resolve();
  }

  const loadPromise = new Promise<void>((resolve, reject) => {
    trackerLoadStarted = true;
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Unable to load ${id}.`));
    document.head.appendChild(script);
  });
  scriptLoads.set(id, loadPromise);
  return loadPromise;
}

function initializeGoogleAds() {
  if (googleAdsInitialized) {
    return;
  }
  const currentWindow = trackingWindow();
  currentWindow.dataLayer = currentWindow.dataLayer || [];
  currentWindow.gtag = currentWindow.gtag || ((...args: unknown[]) => {
    currentWindow.dataLayer?.push(args);
  });
  currentWindow.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
  currentWindow.gtag('js', new Date());
  currentWindow.gtag('config', GOOGLE_ADS_ID, { send_page_view: false });
  googleAdsInitialized = true;
}

function sanitizedPageLocation(pathname: string): string {
  return `${window.location.origin}${pathname}`;
}

function canEnableTracking(pathname: string, preference: 'analytics' | 'marketing'): boolean {
  return PRODUCTION_HOSTS.has(window.location.hostname)
    && window.location.pathname === pathname
    && isThirdPartyTrackingRoute(pathname)
    && readCookiePreferences()[preference];
}

function trackGooglePageView(pathname: string) {
  if (lastGoogleTrackedPath === pathname) {
    return;
  }
  trackingWindow().gtag?.('event', 'page_view', {
    page_location: sanitizedPageLocation(pathname),
    page_path: pathname,
    page_title: document.title,
  });
  lastGoogleTrackedPath = pathname;
}

async function enableGoogleConsentMode(pathname: string, generation: number) {
  initializeGoogleAds();
  await loadScript(
    'lovibook-google-ads',
    `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`,
  );
  if (generation !== trackingGeneration
      || !PRODUCTION_HOSTS.has(window.location.hostname)
      || window.location.pathname !== pathname
      || (!isThirdPartyTrackingRoute(pathname)
        && !EVENT_ONLY_GOOGLE_TRACKING_ROUTES.has(pathname))) {
    return;
  }
  googleTrackingReady = true;
  if (readCookiePreferences().marketing) {
    trackingWindow().gtag?.('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: readCookiePreferences().analytics ? 'granted' : 'denied',
    });
  }
  pendingGoogleEvents.splice(0).forEach(({ event, resolve }) => {
    void Promise.resolve(event()).finally(resolve);
  });
  if (isThirdPartyTrackingRoute(pathname) && !readCookiePreferences().marketing) {
    trackGooglePageView(pathname);
  }
}

async function enableMarketingTracking(pathname: string, generation: number) {
  initializeGoogleAds();
  await Promise.allSettled([
    loadScript('lovibook-meta-pixel', '/meta-pixel.js'),
    loadScript('lovibook-tiktok-pixel', '/tiktok-pixel.js'),
    loadScript('lovibook-google-ads', `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`),
  ]);

  if (generation !== trackingGeneration || !canEnableTracking(pathname, 'marketing')) {
    return;
  }
  const currentWindow = trackingWindow();
  marketingTrackingReady = true;
  currentWindow.fbq?.('consent', 'grant');
  currentWindow.ttq?.grantConsent?.();
  currentWindow.gtag?.('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: readCookiePreferences().analytics ? 'granted' : 'denied',
  });
  pendingMarketingEvents.splice(0).forEach(({ event, resolve }) => {
    void Promise.resolve(event()).finally(resolve);
  });
  trackGooglePageView(pathname);
  if (lastMarketingTrackedPath !== pathname) {
    currentWindow.fbq?.('track', 'PageView');
    currentWindow.ttq?.page?.();
    lastMarketingTrackedPath = pathname;
  }
}

async function enableAnalyticsTracking(pathname: string, generation: number) {
  await loadScript('lovibook-clarity', '/clarity.js');
  if (generation !== trackingGeneration || !canEnableTracking(pathname, 'analytics')) {
    return;
  }
  trackingWindow().clarity?.('consentv2', {
    ad_Storage: 'denied',
    analytics_Storage: 'granted',
  });
}

function revokeTrackingConsent(preferences: CookiePreferences) {
  const currentWindow = trackingWindow();
  if (!preferences.marketing) {
    marketingTrackingReady = false;
    pendingMarketingEvents.splice(0).forEach(({ resolve }) => resolve());
    currentWindow.fbq?.('consent', 'revoke');
    currentWindow.ttq?.revokeConsent?.();
    currentWindow.gtag?.('consent', 'update', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
  }
  if (!preferences.analytics) {
    currentWindow.clarity?.('consentv2', {
      ad_Storage: 'denied',
      analytics_Storage: 'denied',
    });
  }
}

export function configureThirdPartyTracking(pathname: string) {
  trackingGeneration += 1;
  const generation = trackingGeneration;
  if (!PRODUCTION_HOSTS.has(window.location.hostname) || !isThirdPartyTrackingRoute(pathname)) {
    googleTrackingReady = false;
    pendingGoogleEvents.splice(0).forEach(({ resolve }) => resolve());
    revokeTrackingConsent({
      ...readCookiePreferences(),
      analytics: false,
      marketing: false,
    });
    return;
  }

  const preferences = readCookiePreferences();
  revokeTrackingConsent(preferences);
  void enableGoogleConsentMode(pathname, generation).catch(() => undefined);
  if (preferences.analytics) {
    void enableAnalyticsTracking(pathname, generation).catch(() => undefined);
  }
  if (preferences.marketing) {
    void enableMarketingTracking(pathname, generation).catch(() => undefined);
  }
}
